import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaPlay, FaPause, FaStepBackward, FaStepForward, FaVolumeUp, FaVolumeMute, FaList, FaTimes } from 'react-icons/fa';

interface Song {
  id: number;
  title: string;
  artist: string;
  audioSrc: string;
  imageSrc?: string;
}

interface MusicPlayerProps {
  songs: Song[];
  autoPlay?: boolean;
  defaultImage?: string;
}

const MusicPlayer: React.FC<MusicPlayerProps> = ({
                                                   songs,
                                                   autoPlay = false,
                                                   defaultImage = "/assets/images/unknown.png"
                                                 }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [volume, setVolume] = useState(() => {
    const savedVolume = localStorage.getItem('musicPlayerVolume');
    return savedVolume ? parseFloat(savedVolume) : 1;
  });
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const volumeTimeout = useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Сохранение громкости в localStorage
  useEffect(() => {
    localStorage.setItem('musicPlayerVolume', volume.toString());
  }, [volume]);

  const togglePlay = useCallback(async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        await audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.error("Error toggling play state:", error);
    }
  }, [isPlaying]);

  const nextSong = useCallback(() => {
    setIsLoading(true);
    setCurrentSongIndex((prevIndex) => (prevIndex + 1) % songs.length);
  }, [songs.length]);

  const prevSong = useCallback(() => {
    setIsLoading(true);
    setCurrentSongIndex((prevIndex) => (prevIndex - 1 + songs.length) % songs.length);
  }, [songs.length]);

  // Управление с клавиатуры
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      if (!containerRef.current?.contains(document.activeElement)) return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (audioRef.current) {
            audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 5);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (audioRef.current) {
            audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 5);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume(prev => Math.min(1, prev + 0.1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume(prev => Math.max(0, prev - 0.1));
          break;
        case 'KeyM':
          e.preventDefault();
          setVolume(prev => prev === 0 ? 1 : 0);
          break;
        case 'KeyL':
          e.preventDefault();
          setShowPlaylist(prev => !prev);
          break;
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [duration, togglePlay]);

  // Предзагрузка изображений
  useEffect(() => {
    songs.forEach(song => {
      if (song.imageSrc) {
        const img = new Image();
        img.src = song.imageSrc;
      }
    });
  }, [songs]);

  // Обработка событий аудио
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };
    const handleEnded = () => {
      nextSong();
      if (isPlaying && audio) {
        audio.play().catch(console.error);
      }
    };
    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleWaiting = () => setIsBuffering(true);
    const handlePlaying = () => setIsBuffering(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handlePlaying);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handlePlaying);
    };
  }, [nextSong, isPlaying]);

  // Автовоспроизведение
  useEffect(() => {
    if (autoPlay && audioRef.current) {
      audioRef.current.play().catch(error => console.error("Error auto-playing audio:", error));
    }
  }, [autoPlay]);

  // Управление громкостью
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !audioRef.current) return;

    const rect = progressRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    audioRef.current.currentTime = percentage * duration;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };

  const handleVolumeIconHover = () => {
    clearTimeout(volumeTimeout.current);
    setShowVolumeSlider(true);
  };

  const handleVolumeIconLeave = () => {
    volumeTimeout.current = setTimeout(() => {
      setShowVolumeSlider(false);
    }, 1000);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
      <div
          ref={containerRef}
          className="p-3 rounded-xl transition-all duration-300"
          style={{
            borderColor: 'rgb(255, 255, 255)',
            borderWidth: '2px',
            backgroundColor: 'rgba(66, 66, 66, 0.9)',
          }}
          tabIndex={0}
          role="region"
          aria-label="Music Player"
      >
        <div className="flex items-center gap-4">
          {/* Обложка альбома */}
          <div className="relative flex-shrink-0">
            <img
                src={songs[currentSongIndex].imageSrc || defaultImage}
                alt={`Album art for ${songs[currentSongIndex].title}`}
                className={`w-20 h-20 rounded-lg object-cover transition-opacity duration-300 ${
                    isLoading ? 'opacity-50' : 'opacity-100'
                }`}
                onError={(e) => {
                  e.currentTarget.src = defaultImage;
                }}
            />
            {(isLoading || isBuffering) && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white"></div>
                </div>
            )}
          </div>

          {/* Информация о треке и управление */}
          <div className="flex-grow flex flex-col gap-2">
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold text-white truncate">
                {songs[currentSongIndex].title}
              </h2>
              <p className="text-sm text-gray-300 truncate">
                {songs[currentSongIndex].artist}
              </p>
            </div>

            {/* Прогресс-бар */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-300">{formatTime(currentTime)}</span>
              <div
                  className="flex-grow relative h-1 bg-gray-600 rounded-full cursor-pointer"
                  onClick={handleProgressClick}
                  ref={progressRef}
              >
                <div
                    className="absolute h-full bg-white rounded-full"
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                />
              </div>
              <span className="text-xs text-gray-300">{formatTime(duration)}</span>
            </div>

            {/* Кнопки управления */}
            <div className="flex items-center justify-center gap-4">
              <button
                  onClick={prevSong}
                  className="text-white hover:text-gray-300 transition-all duration-300"
                  aria-label="Previous song"
              >
                <FaStepBackward size={16} />
              </button>

              <button
                  onClick={togglePlay}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-black hover:bg-gray-200 transition-all duration-300"
                  disabled={isLoading}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-black"></div>
                ) : (
                    isPlaying ? <FaPause size={14} /> : <FaPlay size={14} className="ml-0.5" />
                )}
              </button>

              <button
                  onClick={nextSong}
                  className="text-white hover:text-gray-300 transition-all duration-300"
                  aria-label="Next song"
              >
                <FaStepForward size={16} />
              </button>
            </div>
          </div>

          {/* Громкость и плейлист */}
          <div className="flex items-center gap-4">
            <div
                className="relative"
                onMouseEnter={handleVolumeIconHover}
                onMouseLeave={handleVolumeIconLeave}
            >
              <button
                  onClick={() => setVolume(volume === 0 ? 1 : 0)}
                  className="text-white hover:text-gray-300 transition-all duration-300"
                  aria-label={volume === 0 ? 'Unmute' : 'Mute'}
              >
                {volume === 0 ? <FaVolumeMute size={16} /> : <FaVolumeUp size={16} />}
              </button>

              <div
                  className={`absolute bottom-full right-0 mb-2 transition-all duration-300 ${
                      showVolumeSlider ? 'opacity-100 visible' : 'opacity-0 invisible'
                  }`}
              >
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-20 h-1 appearance-none bg-gray-600 rounded-full cursor-pointer rotate-270"
                    aria-label="Volume"
                    style={{
                      WebkitAppearance: 'none',
                      background: `linear-gradient(to right, white ${volume * 100}%, #4B5563 ${volume * 100}%)`
                    }}
                />
              </div>
            </div>

            <button
                onClick={() => setShowPlaylist(!showPlaylist)}
                className="text-white hover:text-gray-300 transition-all duration-300"
                aria-label="Toggle playlist"
                aria-expanded={showPlaylist}
            >
              {showPlaylist ? <FaTimes size={16} /> : <FaList size={16} />}
            </button>
          </div>
        </div>

        {/* Плейлист */}
        {showPlaylist && (
            <div className="mt-3 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-700">
              {songs.map((song, index) => (
                  <div
                      key={song.id}
                      onClick={() => {
                        setCurrentSongIndex(index);
                        if (isPlaying && audioRef.current) {
                          audioRef.current.play().catch(console.error);
                        }
                      }}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all duration-300 ${
                          currentSongIndex === index ? 'bg-gray-700' : 'hover:bg-gray-800'
                      }`}
                  >
                    <img
                        src={song.imageSrc || defaultImage}
                        alt={`Album art for ${song.title}`}
                        className="w-8 h-8 rounded object-cover"
                        onError={(e) => {
                          e.currentTarget.src = defaultImage;
                        }}
                    />
                    <div className="flex-grow">
                      <div className="text-sm font-medium text-white truncate">{song.title}</div>
                      <div className="text-xs text-gray-400 truncate">{song.artist}</div>
                    </div>
                  </div>
              ))}
            </div>
        )}

        {/* Аудио элемент */}
        <audio
            ref={audioRef}
            src={songs[currentSongIndex].audioSrc}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
        />
      </div>
  );
};

export default MusicPlayer;